<template>
  <form
    class="s-contact"
    data-gtm-container="contact-form"
    data-tool="solvari-contact-form"
    novalidate
    @submit.prevent="handleSubmit(onSubmit)"
  >
    <fieldset class="s-contact__input s-contact__input--name">
      <STextInput
        v-model="firstName"
        :label="tr('contact_form.fields.first_name.label')"
        :maxlength="255"
        name="firstName"
        required
        show-required-type="none"
      />
      <STextInput
        v-model="lastName"
        :label="tr('contact_form.fields.last_name.label')"
        :maxlength="255"
        name="lastName"
        required
        show-required-type="none"
      />
    </fieldset>
    <SEmail
      v-model="email"
      class="s-contact__input"
      :error-label="tr('contact_form.fields.email.error_label')"
      :label="tr('contact_form.fields.email.label')"
      name="email"
      required
      show-required-type="none"
      :typo-correction="locale"
    />
    <SSelect
      v-model="subject"
      class="s-contact__input"
      :items="subjectItems"
      :label="tr('contact_form.fields.subject.label')"
      name="subject"
      :placeholder="tr('contact_form.fields.subject.placeholder')"
      required
      show-required-type="none"
    />
    <STextarea
      v-model="description"
      class="s-contact__input"
      :label="tr('contact_form.fields.description.label')"
      :maxlength="950"
      name="description"
      required
      show-required-type="none"
    />

    <SCheckbox
      v-model="isSubscribedToNewsletter"
      class="s-contact__input"
      :label="tr('contact_form.fields.newsletter.label')"
      name="newsletter"
    />

    <SButton
      v-if="!isSuccessful"
      class="s-contact__submit"
      :loading="isLoading"
      suffix-icon="fa-regular fa-arrow-right"
      type="submit"
    >
      {{ tr("contact_form.submit") }}
    </SButton>
    <ContactSuccess v-if="isSuccessful" />
    <ContactFailed v-else-if="hasFailed" />
  </form>
</template>

<script setup lang="ts">
import { useMutation } from "@pinia/colada";
import { v4 as uuidv4 } from "uuid";
import { computed, ref } from "vue";

import {
  createArgusEvent,
  createGtmEvent,
  getMetadataFromArgus,
} from "@solvari/argus";
import { SButton, SCheckbox, SSelect, STextarea } from "@solvari/common-fe";
import { useValidationObserver } from "@solvari/common-fe/validation";
import { SEmail, STextInput } from "@solvari/components";
import { injectI18nLocale, useI18nContactForm } from "@solvari/translations";

import { submit } from "@/api/contact.api";
import ContactFailed from "@/components/ContactFailed.vue";
import ContactSuccess from "@/components/ContactSuccess.vue";

const locale = injectI18nLocale();
const { tr } = useI18nContactForm();

const { isLoading, status, mutate } = useMutation({
  ...submit,
  onSuccess: (
    _data,
    {
      uuid,
      firstName,
      lastName,
      email,
      subject,
      description,
      isSubscribedToNewsletter,
    },
  ) =>
    Promise.allSettled([
      createGtmEvent("form_submit", {
        uuid,
        firstName,
        lastName,
        email,
        subject,
        description,
        isSubscribedToNewsletter,
      }),
      createArgusEvent("form_submit", { type: "contact", id: uuid }),
    ]),
});

const uuid = uuidv4();
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const description = ref("");

const subject = ref("");
const subjectItems = computed(() =>
  (["info_requested", "collaboration", "bug_report", "other"] as const).map(
    (subjectItem) => tr(`contact_form.subjects.${subjectItem}`),
  ),
);

const isSubscribedToNewsletter = ref(false);

const isSuccessful = computed(() => status.value === "success");
const hasFailed = computed(() => status.value === "error");

const { handleSubmit } = useValidationObserver();

async function onSubmit() {
  const formData = {
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    subject: subject.value,
    description: description.value,
    isSubscribedToNewsletter: isSubscribedToNewsletter.value,
  };

  const metadata = await getMetadataFromArgus();

  mutate({ uuid, ...formData, ...metadata });
}
</script>

<style lang="postcss">
/* stylelint-disable solvari/bem-block -- generic filename: no component name to derive the BEM block from */
.s-contact {
  &__input {
    @apply mb-6;

    &--name {
      @apply grid gap-6 grid-cols-auto-64 grid-cols-auto-fit grid-cols-auto-grow;
    }
  }

  &__submit {
    @apply ml-auto;
  }
}
</style>
